import EmbeddableCheckbox from "@components/C138_EmbeddableForm/EmbeddableCheckbox";
import Cta from "@components/Cta/Cta";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { useSettings } from "@utilities/context/settings";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import { ProgrammeContext } from "../../context/programmeComparison";
import IconChevron from "../../public/image/svg/chevron.svg";
import IconExternal from "../../public/image/svg/external.svg";
import { ItemState } from "./Accordion";
import styles from "./C11_VerticalAccordion.module.scss";

export interface AccordionItemProgrammeProps {
  accordionState: ItemState;
  item: number;
  toggleAccordion: (event, item) => void;
  isCompare?: boolean;
  programme: ProgrammeDetails;
}

export const AccordionItemProgramme = (props: AccordionItemProgrammeProps) => {
  const { accordionState, item, toggleAccordion, isCompare, programme } = props;
  const [isVisible, setVisibility] = useState(false);
  const [contentHeight, setContentHeight] = useState(
    accordionState.contentHeight
  );
  const [checked, setChecked] = useState(false);

  const { programmeSettings } = useSettings();

  const {
    programmeDetailsExternalLink,
    programmeTitle,
    programmeDescription,
    programmeLocationInfo,
    slug,
    programmeTypeCode,
    type,
    programmeImportData,
  } = programme;

  const {
    programmeNextStartDateShortLabel,
    programmeDurationLabel,
    programmeLocationLabel,
    programmeFeesLabel,
    programmeInfoCtaLabel,
    fallbackProgrammeIterationFee,
    fallbackProgrammeIterationDuration,
    fallbackProgrammeIterationLocation,
    noProgrammeDateAvailableMessage,
  } = programmeSettings;

  const getFormattedDateFromISO = (datetime) => {
    const date = new Date(datetime);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
    };
    return date.toLocaleDateString(undefined, options);
  };

  const { programmes, addProgramme, removeProgramme } =
    useContext(ProgrammeContext);

  useEffect(() => {
    setChecked(!!programmes.find((p) => p.programmeCode === programmeTypeCode));
  }, [programmes]);

  useEffect(() => {
    // Set height for animation
    setContentHeight(accordionState.contentHeight);

    // If it's open, set visibility and set content to auto so it can be responsive on resize
    if (accordionState.isOpen) {
      setVisibility(true);

      setTimeout(() => {
        setContentHeight("auto");
      }, 300);
    } else {
      setTimeout(() => {
        setContentHeight("0");
      }, 10);

      setTimeout(() => {
        setVisibility(false);
      }, 300);
    }
  }, [accordionState.isOpen]);

  const programmeAccordionClassName =
    programmeTypeCode === "EMBAG"
      ? styles["accordion-embag"]
      : styles["accordion-programme"];

  return (
    <li className={programmeAccordionClassName}>
      {programmeDetailsExternalLink && programmeTypeCode === "EMBAG" ? (
        // <div className={styles["external-link-container"]}>
        <>
          <a
            href={programmeDetailsExternalLink}
            target="_blank"
            rel="noreferrer"
            className={styles["external-link"]}
          >
            <IconExternal />
          </a>
          <button
            className={styles["toggle-link"]}
            aria-expanded={isVisible}
            onClick={(e) => {
              toggleAccordion(e, item);
            }}
          >
            <div className={styles["top-section"]}>
              <div className={styles["padded-wrapper"]}>
                <span>{programmeTitle}</span>
                <div className="tick icon-chevron">
                  <IconChevron />
                </div>
              </div>
            </div>
          </button>
        </>
      ) : programmeDetailsExternalLink && programmeTypeCode !== "EMBAG" ? (
        <>
          <button
            className={styles["toggle-link"]}
            aria-expanded={isVisible}
            onClick={(e) => {
              toggleAccordion(e, item);
            }}
          >
            <div className={styles["top-section"]}>
              <div className={styles["padded-wrapper"]}>
                <span>{programmeTitle}</span>
                <div className="tick icon-chevron">
                  <IconChevron />
                </div>
              </div>
            </div>
          </button>
        </>
      ) : (
        ///* </div> */}

        <button
          className={styles["toggle-link"]}
          aria-expanded={isVisible}
          onClick={(e) => {
            toggleAccordion(e, item);
          }}
          data-testid="accordion-toggle"
        >
          <div className={styles["top-section"]}>
            <div className={styles["padded-wrapper"]}>
              <h4>{programmeTitle}</h4>
              <div className="tick icon-chevron">
                <IconChevron />
              </div>
            </div>
          </div>
        </button>
      )}
      <div
        className={styles["accordion-drop"]}
        aria-hidden={!isVisible}
        style={{
          visibility: isVisible ? "visible" : "hidden",
          height: contentHeight,
        }}
      >
        <div
          className={clsx(
            styles["content"],
            styles["inner-wrapper"],
            styles["padded-wrapper"]
          )}
        >
          <div className={styles["description"]}>
            <p>{programmeDescription}</p>
          </div>
          <div className={styles["section-row-wrapper"]}>
            <div className={styles["section-row"]}>
              <span className={styles["label"]}>
                {programmeNextStartDateShortLabel}
              </span>
              <span className={styles["info"]}>
                {programmeImportData
                  ? programmeImportData[0]?.programmeInclusiveDates ||
                    getFormattedDateFromISO(
                      programmeImportData[0].programmeStartDate
                    )
                  : noProgrammeDateAvailableMessage}
                {/* TODO: Add when Programme Iteration */}
                {/* {programmeImportData?.programmeAvailabilityString && (
                  <span className={styles["sub-text"]}>
                    {programmeImportData?.programmeAvailabilityString}
                  </span>
                )} */}
              </span>
            </div>
            <div className={styles["section-row"]}>
              <span className={styles["label"]}>{programmeDurationLabel}</span>
              <span className={styles["info"]}>
                {programmeImportData
                  ? programmeImportData[0]?.programmeDuration
                  : fallbackProgrammeIterationDuration}
              </span>
            </div>
            <div className={styles["section-row"]}>
              <span className={styles["label"]}>{programmeLocationLabel}</span>
              <div className={styles["info"]}>
                {programmeImportData
                  ? programmeImportData[0]?.programmeLocation
                  : fallbackProgrammeIterationLocation}
                {programmeLocationInfo && (
                  <div
                    className={clsx(styles["message-wrapper"], styles["light"])}
                    aria-describedby="message1"
                  >
                    <p className={styles["message"]} id="message1">
                      {programmeLocationInfo}
                      {/* <a href="#close" aria-label="Close" class="icon-cross" data-grunticon-embed=""></a> */}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className={styles["section-row"]}>
              <span className={styles["label"]}>{programmeFeesLabel}</span>
              <div className={styles["info"]}>
                {programmeImportData
                  ? programmeImportData[0]?.programmeFee
                  : fallbackProgrammeIterationFee}
                {/* TODO: Add when Programme Iteration */}
                {/* {programmeImportData?.programmeFeeInfo && (
                  <div
                    className={`${styles["message-wrapper"]} ${styles["light"]}`}
                    tabIndex={0}
                    aria-describedby="message2"
                  >
                    <p className={styles["message"]} id="message2">
                      {programmeImportData?.programmeFeeInfo}
                      <a href="#close" aria-label="Close" class="icon-cross" data-grunticon-embed=""></a>
                    </p>
                  </div>
                )} */}
              </div>
            </div>
            <div className={styles["button-wrapper"]}>
              <Cta
                type="tertiary"
                url={
                  programme?.programmeDetailsExternalLink
                    ? programme?.programmeDetailsExternalLink
                    : slug || "#"
                }
                linkText={programmeInfoCtaLabel || ""}
                icon="icon-arrow"
                openInNewTab={
                  programmeDetailsExternalLink && programmeTypeCode === "EMBAG"
                    ? !!programmeDetailsExternalLink
                    : !programmeDetailsExternalLink
                }
              />
            </div>
          </div>
        </div>
      </div>
      {isCompare && (
        <div className={clsx(styles["compare-option"], "form")}>
          <EmbeddableCheckbox
            id={programmeTypeCode}
            label="Select up to 4 to compare"
            checked={checked}
            onChange={(event) => {
              setChecked(event.target.checked);
              if (event.target.checked) {
                addProgramme({
                  programmeTitle,
                  programmeCode: programmeTypeCode,
                  programmeType: type,
                });
                return;
              }

              removeProgramme({
                programmeTitle,
                programmeCode: programmeTypeCode,
              });
            }}
          />
        </div>
      )}
    </li>
  );
};

export default AccordionItemProgramme;
